/**
 * Попереднє завантаження КОДУ маршрутів (UX-33; speed.md, розд. UX-33; docs/dev/web-speed.md).
 * Лише чанки: `beforeLoad` і `loader` маршрутів не викликаємо, тож запитів даних і побічних дій
 * (тост, синхронізація профілю, аудит GET) немає. Дані наперед — Next.
 *
 * Два шляхи:
 * - **простій** після першого екрана (`useIdlePrefetch`, perf/prefetch.ts — як і коли): палітра
 *   (Ctrl+K ≤ 100 мс, performance-budget.md, розд. 3.2) і маршрути, куди зі стартового екрана йдуть
 *   найчастіше — картка запису, а після входу ще й список подання;
 * - **намір** (`useIntentPreload`): наведення понад 50 мс, фокус чи дотик на посиланні — код того
 *   маршруту. Так вантажаться рідші й більші розділи (адмінка, профіль).
 *
 * Легкий режим і «Економія трафіку» не вантажать нічого (lite-mode.md, розд. 1). Маршрути беремо з
 * роутера за id чи адресою, а не імпортом модулів `routes/`: ті імпортують оболонку, вийшов би цикл.
 */
import { type AnyRouter, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useRef } from 'react';
import { prefetchPaletteChunk } from '../palette/CommandHost.tsx';
import { browserEnv, type PrefetchEnv, scheduleIdlePrefetch } from '../perf/prefetch.ts';
import { prefetchQuickCreate } from '../platform/quick-create-load.ts';
import { getPrefs, useCapabilities } from '../prefs/prefs.ts';

/**
 * Id маршрутів для простою після входу. Список подання — у першому екрані (routes/platform.ts),
 * наперед вантажимо лише картку. Без входу оболонки немає (гість іде на вхід), тож і простою теж.
 */
export const IDLE_ROUTES = ['/r/$entity/$recordId'] as const;

/** Затримка наведення до завантаження (як `defaultPreloadDelay` TanStack Router). */
export const INTENT_DELAY_MS = 50;

/** Код маршруту (лінивий `.lazy()` і компоненти, `loadRouteChunk`). Невідомий id — нічого. */
export function loadRouteCode(router: AnyRouter, routeId: string): Promise<unknown> {
  const route = router.routesById[routeId];
  return route ? Promise.resolve(router.loadRouteChunk(route)) : Promise.resolve();
}

/** Код маршруту за адресою посилання: уся гілка від кореня. */
export function preloadRouteCode(router: AnyRouter, href: string): Promise<unknown> {
  const pathname = href.split(/[?#]/)[0] ?? '';
  const [branch] = router.getMatchedRoutes(pathname);
  return Promise.all(branch.map((route) => Promise.resolve(router.loadRouteChunk(route))));
}

/**
 * Мітки `prefetch:*` — по одній на документ. Якщо `signedIn` змінився вже після старту, ефект
 * запускається знову, і без цього мітка `prefetch:start` лягла б удруге та зсунула б межу замірів.
 */
export function onceMarks(env: PrefetchEnv, seen = new Set<string>()): PrefetchEnv {
  return {
    ...env,
    mark: (name) => {
      if (seen.has(name)) return;
      seen.add(name);
      env.mark(name);
    },
  };
}

const documentEnv = onceMarks(browserEnv);

export function useIdlePrefetch(signedIn: boolean) {
  const router = useRouter();
  useEffect(() => {
    if (!signedIn) return;
    return scheduleIdlePrefetch(
      [
        prefetchPaletteChunk,
        // Швидке створення: діалогом користуються весь день (DAT-01).
        prefetchQuickCreate,
        ...IDLE_ROUTES.map((id) => () => loadRouteCode(router, id)),
      ],
      { lite: getPrefs().lite, env: documentEnv },
    );
  }, [router, signedIn]);
}

export interface IntentHandlers {
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  onFocus?: () => void;
  onTouchStart?: () => void;
}

/**
 * Обробники наміру для посилання на `href`: наведення понад 50 мс (пройти курсором повз —
 * не намір), фокус і дотик — одразу. Повторний намір нічого не шле: чанк уже в кеші модулів.
 * Легкий режим — без обробників.
 */
export function useIntentPreload(href: string): IntentHandlers {
  const router = useRouter();
  const { prefetch } = useCapabilities();
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  return useMemo(() => {
    if (!prefetch) return {};
    const now = () => {
      clearTimeout(timer.current);
      if (browserEnv.saveData()) return;
      void preloadRouteCode(router, href).catch(() => undefined);
    };
    return {
      onPointerEnter: () => {
        clearTimeout(timer.current);
        timer.current = setTimeout(now, INTENT_DELAY_MS);
      },
      onPointerLeave: () => clearTimeout(timer.current),
      onFocus: now,
      onTouchStart: now,
    };
  }, [prefetch, router, href]);
}
